import {
  Component,
  effect,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
} from '@angular/core';
import { DropdownModule } from 'primeng/dropdown';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { PrimeTemplate } from 'primeng/api';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { Bookmark } from '../../../models/bookmark';
import { getState } from '@ngrx/signals';

@Component({
  selector: 'app-bookmark-dropdown',
  standalone: true,
  imports: [
    DropdownModule,
    FormFieldWrapperComponent,
    PrimeTemplate,
    FormsModule,
  ],
  templateUrl: './bookmark-dropdown.component.html',
  styleUrl: './bookmark-dropdown.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BookmarkDropdownComponent),
      multi: true,
    },
  ],
})
export class BookmarkDropdownComponent {
  readonly bookmarkStore = inject(BookmarkStore);

  @Input() label = 'Ansicht';
  @Input() name = 'bookmark';
  @Input() selection: Bookmark | null = null;
  @Input() disabled = false;
  @Input() colSize: string | number = 12;
  @Input() enableEmpty = true;
  @Output() selectionChange = new EventEmitter<Bookmark | null>();

  touched = false;
  bookmarks: Bookmark[] = [];

  constructor() {
    effect(() => {
      const bookmarkState = getState(this.bookmarkStore);
      this.bookmarks = this.bookmarkStore.asArray();
    });
  }

  /**
   * A callback function triggered when a value change occurs.
   *
   * This function takes a `value` parameter of type `Bookmark` or `null`.
   * It emits the updated value via the `selectionChange` event emitter
   * after a slight delay of 10ms.
   *
   * @param {Bookmark|null} value - The updated value to emit, can be a Bookmark object or null.
   */
  protected onChange = (value: Bookmark | null): void => {
    setTimeout(() => {
      this.selectionChange.emit(value);
    }, 10);
  };

  /**
   * Handles the selection change event, updates the selection property,
   * and notifies the form control of the change.
   *
   * @param {any} event - The event object containing the new selection value.
   * @return {void} No return value.
   */
  onSelectionChange(event: any): void {
    this.selection = event.value;
    this.onChange(this.selection); // Notify the form control of the change
    this.onTouched();
  }

  private onTouched = () => {
    this.touched = false;
  };

  writeValue(value: Bookmark): void {
    this.selection = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
